import useExploreInfo from "Hooks/useExploreInfo";
import styles from "./FeatureEntities.module.scss";
import useFilterAndPagination from "Hooks/useFilterAndPagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  getAllInstitutionExplore,
  getInstitutionTopRankingKeywords,
  getSearchInstitutions,
} from "Explore/Services/Institution";
import {
  DEFAULT_SEARCH_PARAMS,
  ParamsFilterKey,
} from "Config/filterSearchParams";
import FeatureInstitutionItem from "./FeatureInstitutionItem";
import FeatureVenueItem from "./FeatureVenueItem";
import { getAllVenueExplore, getSearchVenues } from "Explore/Services/Venue";
import {
  getExampleConceptExplore,
  getSearchConcepts,
} from "Explore/Services/Concept";
import FeatureConceptItem from "./FeatureConceptItem";
import _ from "lodash";
import { getRelatedKeywords } from "Explore/Services/Common";
import { MAP_RELATED_KEYWORDS_ENTITY } from "Explore/Utils";

const MAP_ENTITY = {
  faculty: {
    name: "faculty",
    filterKeys: [],
  },
  institution: {
    name: "institution",
    filterKeys: [ParamsFilterKey.KEYWORD_QUERY],
  },
  venue: {
    name: "venue",
    filterKeys: [ParamsFilterKey.KEYWORD_QUERY],
  },
  concept: {
    name: "concept",
    filterKeys: [ParamsFilterKey.KEYWORD_QUERY],
  },
};

const DEFAULT_LOADING_LIST = Array.from({ length: 3 });

const FeatureEntities = () => {
  const { entity_type } = useExploreInfo();
  const { filter } = useFilterAndPagination();
  const [faculty, setFaculty] = useState({
    data: DEFAULT_LOADING_LIST,
    isLoading: true,
  });
  const [institution, setInstitution] = useState({
    data: DEFAULT_LOADING_LIST,
    isLoading: true,
  });
  const [venue, setVenue] = useState({
    data: DEFAULT_LOADING_LIST,
    isLoading: true,
  });
  const [concept, setConcept] = useState({
    data: DEFAULT_LOADING_LIST,
    isLoading: true,
  });
  const [expanded, setExpanded] = useState({
    institution: true,
    venue: true,
    concept: true,
  });
  const prevFilter = useRef(null);

  const toggleExpanded = (panel) => {
    setExpanded((prev) => ({ ...prev, [panel]: !prev[panel] }));
  };

  const getIsFilteredByEntity = useCallback(
    (entity) => {
      return MAP_ENTITY[entity].filterKeys.some(
        (key) =>
          (Array.isArray(filter[key]) && filter[key].length > 0) ||
          filter[key] !== DEFAULT_SEARCH_PARAMS[key]
      );
    },
    [filter]
  );

  const fetchFaculty = () => {
    setFaculty({ data: DEFAULT_LOADING_LIST, isLoading: true });
  };

  const fetchInstitution = (filter) => {
    const isFiltered = getIsFilteredByEntity("institution");

    setInstitution({ data: DEFAULT_LOADING_LIST, isLoading: true });
    const callback = async (res) => {
      const newData = await Promise.allSettled(
        res.results.map(async (e) => {
          const _e = { ...e };
          try {
            const res = await getInstitutionTopRankingKeywords({
              institution_id: e.id,
            });
            _e.rankingKeywords = res.top_ranking_keywords;
            return _e;
          } catch (error) {
            return _e;
          }
        })
      );
      setInstitution({ data: newData.map((e) => e.value), isLoading: false });
    };
    isFiltered
      ? getSearchInstitutions({ ...filter, page: 0, rowsPerPage: 3 }, callback)
      : getAllInstitutionExplore(0, 3, callback);
  };

  const fetchVenue = () => {
    const isFiltered = getIsFilteredByEntity("venue");
    setVenue({ data: DEFAULT_LOADING_LIST, isLoading: true });
    const callback = (res) => {
      setVenue({ data: res.results, isLoading: false });
    };
    isFiltered
      ? getSearchVenues({ ...filter, page: 0, rowsPerPage: 3 }, callback)
      : getAllVenueExplore(0, 3, callback);
  };

  const fetchConcept = () => {
    const isFiltered = getIsFilteredByEntity("concept");
    setConcept({ data: DEFAULT_LOADING_LIST, isLoading: true });
    const callback = (res) => {
      setConcept({ data: res.results, isLoading: false });
    };
    isFiltered
      ? getRelatedKeywords({
        keywords: filter[ParamsFilterKey.KEYWORD_QUERY],
        top_k: 3,
        entity: MAP_RELATED_KEYWORDS_ENTITY[entity_type],
      }).then((res) => setConcept({ data: res, isLoading: false }))
      : getExampleConceptExplore(0, 3, callback);
  };

  useEffect(() => {
    const displayEntities = Object.values(MAP_ENTITY)
      .filter((entity) => entity.name !== entity_type)
      .map((entity) => entity.name);

    displayEntities.forEach((entity) => {
      if (entity === MAP_ENTITY.faculty.name) {
        fetchFaculty(filter);
      } else if (entity === MAP_ENTITY.institution.name) {
        if (
          !_.isEqual(
            _.pick(prevFilter.current, MAP_ENTITY.institution.filterKeys),
            _.pick(filter, MAP_ENTITY.institution.filterKeys)
          )
        ) {
          fetchInstitution(filter);
        }
      } else if (entity === MAP_ENTITY.venue.name) {
        if (
          !_.isEqual(
            _.pick(prevFilter.current, MAP_ENTITY.venue.filterKeys),
            _.pick(filter, MAP_ENTITY.venue.filterKeys)
          )
        ) {
          fetchVenue(filter);
        }
      } else if (entity === MAP_ENTITY.concept.name) {
        if (
          !_.isEqual(
            _.pick(prevFilter.current, MAP_ENTITY.concept.filterKeys),
            _.pick(filter, MAP_ENTITY.concept.filterKeys)
          )
        ) {
          fetchConcept(filter);
        }
      }
    });
    prevFilter.current = { ...filter, entity_type };
  }, [entity_type, JSON.stringify(filter)]);

  return (
    <section className={styles["feature-entities"]}>
      <h2 className={styles["title"]}>
        Expand your exploration: Discover Feature Entities or Topics related to
        your current search
      </h2>

      {entity_type !== MAP_ENTITY.institution.name && (
        <div className={styles["list"]}>
          <div
            className={styles["list--title"]}
            onClick={() => toggleExpanded("institution")}
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <p>Feature Institutions</p>
            <IconButton size="small">
              {expanded.institution ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          {expanded.institution &&
            institution.data.map((e, i) => (
              <FeatureInstitutionItem
                key={i}
                data={e}
                isLoading={institution.isLoading}
              />
            ))}
        </div>
      )}
      {entity_type !== MAP_ENTITY.venue.name && (
        <div className={styles["list"]}>
          <div
            className={styles["list--title"]}
            onClick={() => toggleExpanded("venue")}
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <p>Feature Venues</p>
            <IconButton size="small">
              {expanded.venue ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          {expanded.venue &&
            venue.data.map((e, i) => (
              <FeatureVenueItem key={i} data={e} isLoading={venue.isLoading} />
            ))}
        </div>
      )}
      {/* {entity_type !== MAP_ENTITY.concept.name && (
        <div className={styles["list"]}>
          <div
            className={styles["list--title"]}
            onClick={() => toggleExpanded("concept")}
            style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <p>Feature Concepts</p>
            <IconButton size="small">
              {expanded.concept ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </div>
          {expanded.concept &&
            concept.data.map((e, i) => (
              <FeatureConceptItem
                key={i}
                data={e}
                isLoading={concept.isLoading}
              />
            ))}
        </div>
      )} */}
    </section>
  );
};

export default FeatureEntities;
